import React from 'react'
import { Field, Form } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { ClientFormData, validateClientForm } from 'src/helpers/clientFormValidation'
import FormSelect from '../atoms/FormSelect'
import { FormFieldError, FormFieldLabel, FormTextInput } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import BuildingOfficeSvg from '../other/svg/BuildingOfficeSvg'
import CheckSvg from '../other/svg/CheckSvg'
import UserCircleSvg from '../other/svg/UserCircleSvg'
import FormFooter from './FormFooter'

interface ClientFormProps {
  initialValues: ClientFormData
  onSubmit: (values: ClientFormData) => void
  onCancel: () => void
  mode: 'create' | 'edit'
  loading?: boolean
}

interface FieldMeta {
  error?: string
  touched?: boolean
  submitFailed?: boolean
}

const isFieldInvalid = (meta: FieldMeta): boolean =>
  Boolean(meta.error) && (Boolean(meta.touched) || Boolean(meta.submitFailed))

const fieldClass = 'self-stretch flex flex-col justify-start items-start gap-2'

interface ClientTextFieldProps {
  name: keyof ClientFormData
  label: string
  placeholder: string
}

const ClientTextField = ({ name, label, placeholder }: ClientTextFieldProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Field name={name}>
      {({ input, meta }) => (
        <div className={fieldClass}>
          <FormFieldLabel label={label} />
          <FormTextInput
            value={typeof input.value === 'string' ? input.value : ''}
            onChange={input.onChange}
            placeholder={placeholder}
            error={isFieldInvalid(meta)}
          />
          <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
        </div>
      )}
    </Field>
  )
}

const ClientForm = ({ initialValues, onSubmit, onCancel, mode, loading = false }: ClientFormProps): JSX.Element => {
  const { t } = useTranslation()

  return (
    <Form
      onSubmit={onSubmit}
      validate={validateClientForm}
      initialValues={initialValues}
      render={({ handleSubmit, submitting, values, form, dirty }) => (
        <form
          // eslint-disable-next-line @typescript-eslint/no-misused-promises
          onSubmit={handleSubmit}
          className="self-stretch flex-1 min-h-0 relative bg-backgrounds-bg-field-component rounded-lg inline-flex flex-col justify-start items-center overflow-hidden"
          style={{
            boxShadow:
              '0px 4px 8px 0px rgba(0,0,0,0.08), 0px 2px 4px 0px rgba(0,0,0,0.08), 0px 0px 0px 1px rgba(0,0,0,0.08)'
          }}>
          <div className="self-stretch h-px bg-borders-border-base" />
          <div className="w-full max-w-[1000px] min-w-[720px] px-4 pt-4 pb-6 flex-1 min-h-0 flex flex-col justify-start items-start gap-6 overflow-y-auto">
            <div className="text-zinc-900 text-xl font-bold font-Montserrat leading-7">{t('clients.client_data')}</div>
            <InsetSegmentedControl
              value={values.client_type ?? 'company'}
              onChange={(clientType) => {
                form.change('client_type', clientType)
              }}
              options={[
                {
                  value: 'company',
                  label: t('clients.company'),
                  icon: <BuildingOfficeSvg className="w-4 h-4" />
                },
                {
                  value: 'personal',
                  label: t('clients.personal'),
                  icon: <UserCircleSvg className="w-4 h-4" />
                }
              ]}
            />
            <div className="self-stretch grid grid-cols-2 gap-4">
              {values.client_type === 'company' && (
                <>
                  <ClientTextField
                    name="company_name"
                    label={t('clients.company_name')}
                    placeholder={t('clients.company_name_placeholder')}
                  />
                  <ClientTextField
                    name="document_number"
                    label={t('clients.document_number')}
                    placeholder={t('clients.document_number_placeholder')}
                  />
                  <ClientTextField
                    name="vat_number"
                    label={t('clients.vat_number')}
                    placeholder={t('clients.vat_number_placeholder')}
                  />
                </>
              )}
              {values.client_type === 'personal' && (
                <>
                  <ClientTextField
                    name="id_number"
                    label={t('clients.id_number')}
                    placeholder={t('clients.id_number_placeholder')}
                  />
                  <ClientTextField
                    name="document_number"
                    label={t('clients.document_number')}
                    placeholder={t('clients.document_number_placeholder')}
                  />
                  <div />
                </>
              )}
              <Field name="country">
                {({ input, meta }) => (
                  <div className={fieldClass}>
                    <FormFieldLabel label={t('clients.country')} />
                    <FormSelect
                      value={typeof input.value === 'string' ? input.value : ''}
                      onChange={input.onChange}
                      placeholder={t('my_vehicles.choose')}
                      clearable={false}
                      error={isFieldInvalid(meta)}
                      options={[
                        { value: 'poland', label: t('clients.countries.poland') },
                        { value: 'germany', label: t('clients.countries.germany') }
                      ]}
                    />
                    <FormFieldError show={isFieldInvalid(meta)} message={t('my_vehicles.field_required')} />
                  </div>
                )}
              </Field>
              <ClientTextField name="place" label={t('clients.place')} placeholder={t('clients.place_placeholder')} />
              <ClientTextField
                name="zip_code"
                label={t('clients.zip_code')}
                placeholder={t('clients.zip_code_placeholder')}
              />
              <ClientTextField
                name="street"
                label={t('clients.street')}
                placeholder={t('clients.street_placeholder')}
              />
              <ClientTextField
                name="house_number"
                label={t('clients.house_number')}
                placeholder={t('clients.house_number_placeholder')}
              />
              <ClientTextField
                name="first_name"
                label={t('clients.first_name')}
                placeholder={t('clients.first_name_placeholder')}
              />
              <ClientTextField
                name="last_name"
                label={t('clients.last_name')}
                placeholder={t('clients.last_name_placeholder')}
              />
              <ClientTextField name="email" label={t('clients.email')} placeholder={t('clients.email_placeholder')} />
              <ClientTextField
                name="phone_number"
                label={t('clients.phone')}
                placeholder={t('clients.phone_placeholder')}
              />
            </div>
          </div>
          <FormFooter
            cancelLabel={t('my_vehicles.discard')}
            onCancel={onCancel}
            submitLabel={mode === 'edit' ? t('my_vehicles.save') : t('my_vehicles.add')}
            submitDisabled={submitting || loading}
            {...(mode === 'create' ? { submitIcon: <CheckSvg className="w-3.5 h-3.5 text-icon-primary" /> } : {})}
            contentClassName="max-w-[1000px] min-w-[720px]"
            isDirty={dirty}
          />
        </form>
      )}
    />
  )
}

export default ClientForm
